import { useState, useEffect, useMemo, useContext } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import Dinero from 'dinero.js';
import { useApolloClient } from '@apollo/client';
import {
    CreateHoldDocument,
    CreateHoldInput,
    OrderSummaryEventQuery,
    PaymentPageEventQuery,
} from '../../graphql/graphql.ts';

// StyledComponents
import { StyledButton } from '../common/styled/ActionablesJoy.styled.tsx';
import { createFingerPrint } from '../../utils/createFingerPrint.ts';
import { CartContext } from '../providers/cart.tsx';
import {
    formatFlexiblePricingForHold,
    formatFlexiblePricingForTickets,
} from '@/utils/flexiblePricing/flexiblePricing-helper.ts';

interface CheckoutBtnProps {
    history: RouteComponentProps['history'];
    btnText: string;
    isAuthenticated: boolean;
    disabled: boolean;
    backgroundcolor: string;
    event: OrderSummaryEventQuery['event'] | PaymentPageEventQuery['event'];
    checkoutNotes?: string;
    chargeAmount: number;
    chargeCurrency: string;
    organizerPrimaryColor: string;
    onContinueClick?: () => void;
    hasQuestionnaire?: boolean;
}

export const CheckoutBtn = ({
    history,
    btnText,
    disabled,
    backgroundcolor,
    event,
    checkoutNotes,
    chargeAmount,
    chargeCurrency,
    onContinueClick,
    hasQuestionnaire,
}: CheckoutBtnProps) => {
    const { cart, setCart, inputtedUnlockCodes } = useContext(CartContext);
    const [disableBtn, setDisableBtn] = useState<boolean>(disabled);
    const [loading, setLoading] = useState<boolean>(false);
    const [redirectTime, setRedirectTime] = useState<number>(0);
    const apollo = useApolloClient();

    useEffect(() => {
        setDisableBtn(disabled);
    }, [disabled]);

    useEffect(() => {
        if (redirectTime <= 0) return;

        const timer = setInterval(() => {
            setRedirectTime((prevTime) => prevTime - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [redirectTime]);

    const chargeDinero = useMemo(
        () => Dinero({ amount: chargeAmount, currency: chargeCurrency as Dinero.Currency }),
        [chargeCurrency, chargeAmount]
    );

    const onSubmit = async () => {
        setDisableBtn(true);
        setLoading(true);

        try {
            const fingerprint = await createFingerPrint();
            const payload: CreateHoldInput = {
                fingerprint,
                eventId: event?.id,
                unlockCodes: inputtedUnlockCodes,
                flexibleAmounts: formatFlexiblePricingForHold(cart?.flexiblePricing),
                tickets: {
                    ...cart.quantities,
                    ...formatFlexiblePricingForTickets(cart?.flexiblePricing),
                },
                addons: cart.addons,
                charged: {
                    amount: chargeDinero.getAmount(),
                    currency: chargeDinero.getCurrency(),
                },
                promoCodes: cart.promoCodes,
                tosAcceptanceDate: new Date().toISOString(),
            };

            if (event?.checkoutCustomFields?.[0]?.id && checkoutNotes?.trim()) {
                payload.customValues = {
                    [event.checkoutCustomFields[0].id]: checkoutNotes.trim(),
                };
            }

            const { data } = await apollo.mutate({
                mutation: CreateHoldDocument,
                variables: {
                    payload,
                },
            });

            if (!data?.createHold) {
                throw Error('Hold creation failed');
            }

            setCart({
                hold: {
                    id: data.createHold.id,
                    expiresAt: data.createHold.expiresAt,
                    visitorId: fingerprint,
                },
                checkoutNotes,
            });

            setDisableBtn(false);
            setLoading(false);

            if (onContinueClick) {
                onContinueClick();
            }

            if (hasQuestionnaire) {
                history.push('/questionnaire');
            } else {
                history.push('/payment');
            }
        } catch (err) {
            const errorMessage = (err as Error).message;

            alert(`Error creating hold for tickets. ${errorMessage}`);

            setDisableBtn(false);
            setLoading(false);
        }
    };

    return (
        <>
            <StyledButton
                onClick={onSubmit}
                disabled={disableBtn}
                backgroundcolor={backgroundcolor}
                id={'proceed_to_checkout'}
                size="lg"
            >
                {loading ? 'Loading' : btnText}
            </StyledButton>
        </>
    );
};
